import React, { FC, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { bigNumberToText } from '../../utils';
import { CHART_COLORS } from '@silverfort-nodelibs/common/utils/consts';
import moment from 'moment';
import { Checkbox } from '@silverfort/sf-components';
import { COLOR_BY_PROTOCOL } from './consts';

const IdentityDataActivityGraph: FC<{
    daysFilter: string;
    activityByProtocol: { [key: string]: string | number; timestamp: number }[];
}> = ({ activityByProtocol, daysFilter }) => {
    const [visibleProtocols, setVisibleProtocols] = useState<string[]>(Object.keys(COLOR_BY_PROTOCOL));

    const toggleProtocol = (protocol: string) => {
        setVisibleProtocols((prev) =>
            prev.includes(protocol) ? prev.filter((p) => p !== protocol) : [...prev, protocol]
        );
    };

    const relevantProtocols = {};

    for (const activity of activityByProtocol) {
        for (const key in activity) {
            if (COLOR_BY_PROTOCOL[key]) {
                relevantProtocols[key] = COLOR_BY_PROTOCOL[key];
            }
        }
    }

    const getActivityData = () => {
        const relevantStartPoints = {};
        for (const protocol in relevantProtocols) {
            relevantStartPoints[protocol] = 0;
        }

        return [
            {
                ...relevantStartPoints,
                timestamp: moment(Date.now()).subtract(parseInt(daysFilter), 'day').format('MMM DD, YYYY HH:mm')
            },
            ...activityByProtocol.map((item) => ({
                ...item,
                timestamp: moment(new Date(item.timestamp * 1000)).format('MMM DD, YYYY HH:mm')
            }))
        ];
    };

    return (
        <div className="w-full h-[288px] border border-sf-gray-100 hover:border-sf-gray-300 bg-white rounded-xl flex">
            <div className="flex-1">
                <div className="text-13 border-b p-4 h-12 border-sf-gray-100 font-medium font-sf flex items-center">
                    Authentication Protocols over time
                </div>
                <ResponsiveContainer width="100%" height="90%">
                    <LineChart
                        className="text-13"
                        data={getActivityData()}
                        margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis tickFormatter={(value) => moment(value).format('MMM DD, YYYY')} dataKey="timestamp" />
                        <YAxis tickFormatter={(value) => bigNumberToText(value)} />
                        <Tooltip formatter={(value: number) => value.toLocaleString()} />
                        {visibleProtocols.map((protocol) => (
                            <Line
                                key={protocol}
                                type="linear"
                                dataKey={protocol}
                                stroke={relevantProtocols[protocol as keyof typeof relevantProtocols]}
                                strokeWidth={3}
                                dot={false}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="py-4 w-48 pl-4 border-l">
                <div className="font-medium text-sm mb-2">Protocols</div>
                <ul className="space-y-2">
                    {Object.keys(relevantProtocols)
                        .sort()
                        .map((protocol) => (
                            <li key={protocol} className="flex items-center space-x-2">
                                <Checkbox
                                    checked={visibleProtocols.includes(protocol)}
                                    onChange={() => toggleProtocol(protocol)}
                                />
                                <span className="flex items-center">
                                    <span
                                        className="w-3 h-3 inline-block mr-2 rounded"
                                        style={{
                                            backgroundColor:
                                                relevantProtocols[protocol as keyof typeof relevantProtocols]
                                        }}></span>
                                    {protocol.toUpperCase()}
                                </span>
                            </li>
                        ))}
                </ul>
            </div>
        </div>
    );
};

export default IdentityDataActivityGraph;
